import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import {
  useReadManyVmessCategoriesQuery,
  useCreateVmessCategoryMutation,
  useUpdateVmessCategoryMutation,
  useDeleteVmessCategoryMutation,
} from "api/endpoints/vmess-category";
import {
  useReadPaymentSettingsQuery,
  useUpdatePaymentSettingsMutation,
} from "api/endpoints/settings";
import { toast } from "react-toastify";
import { ConfirmationDialogComponent } from "lib/components/ui/confirmation-dialog";
import {
  CATEGORY_LIMIT_HINT,
  formatCategoryLimit,
} from "../../settings/vpn/vpn-settings.utils";

type CategoryForm = {
  name: string;
  price: string;
  gigLimit: string;
  timeLimit: string;
  availableForSale: boolean;
};

const emptyForm: CategoryForm = {
  name: "",
  price: "",
  gigLimit: "",
  timeLimit: "",
  availableForSale: true,
};

export const VmessCategoryListComponent = ({
  embedded = false,
}: {
  embedded?: boolean;
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CategoryForm>(emptyForm);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: settings } = useReadPaymentSettingsQuery();
  const [updateSettings] = useUpdatePaymentSettingsMutation();

  const { data, isLoading, error, refetch } = useReadManyVmessCategoriesQuery({
    query: {
      page: String(page + 1),
      limit: String(rowsPerPage),
    },
  });
  const [createCategory, { isLoading: creating }] =
    useCreateVmessCategoryMutation();
  const [updateCategory, { isLoading: updating }] =
    useUpdateVmessCategoryMutation();
  const [deleteCategory] = useDeleteVmessCategoryMutation();

  const categories = data?.categories || [];
  const totalCount = data?.count || 0;
  const productEnabled = settings?.vmessEnabled ?? true;

  const handleProductToggle = async (enabled: boolean) => {
    try {
      await updateSettings({ payload: { vmessEnabled: enabled } }).unwrap();
      toast.success("وضعیت VMess به‌روزرسانی شد");
    } catch {
      toast.error("خطا در به‌روزرسانی تنظیمات");
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (category: (typeof categories)[0]) => {
    setEditingId(category._id);
    setForm({
      name: category.name,
      price: String(category.price),
      gigLimit: category.gigLimit != null ? String(category.gigLimit) : "",
      timeLimit: category.timeLimit != null ? String(category.timeLimit) : "",
      availableForSale: category.availableForSale,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const payload = {
      name: form.name,
      price: Number(form.price.replace(/,/g, "")),
      gigLimit: form.gigLimit !== "" ? Number(form.gigLimit) : undefined,
      timeLimit: form.timeLimit !== "" ? Number(form.timeLimit) : undefined,
      availableForSale: form.availableForSale,
    };

    try {
      if (editingId) {
        await updateCategory({ params: { categoryId: editingId }, payload }).unwrap();
        toast.success("تعرفه VMess به‌روزرسانی شد");
      } else {
        await createCategory({ payload }).unwrap();
        toast.success("تعرفه VMess ایجاد شد");
      }
      setDialogOpen(false);
      refetch();
    } catch {
      toast.error("خطا در ذخیره تعرفه");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteCategory({ params: { categoryId: deleteId } }).unwrap();
      toast.success("تعرفه VMess حذف شد");
      setDeleteId(null);
      refetch();
    } catch {
      toast.error("خطا در حذف تعرفه");
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={embedded ? 0 : 3}>
      {!embedded && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5">تعرفه‌های VMess</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
            افزودن تعرفه
          </Button>
        </Box>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={productEnabled}
              onChange={(e) => handleProductToggle(e.target.checked)}
            />
          }
          label="نمایش VMess در صفحه خرید"
        />
      </Paper>

      <Alert severity="info" sx={{ mb: 2 }}>
        {CATEGORY_LIMIT_HINT}
      </Alert>

      {embedded && (
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
            افزودن تعرفه
          </Button>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          خطا در بارگذاری تعرفه‌ها
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>نام</TableCell>
                <TableCell>قیمت (تومان)</TableCell>
                <TableCell>حجم (GB)</TableCell>
                <TableCell>مدت (ماه)</TableCell>
                <TableCell>فروش</TableCell>
                <TableCell align="center">عملیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.map((cat) => (
                <TableRow key={cat._id}>
                  <TableCell>{cat.name}</TableCell>
                  <TableCell>{cat.price.toLocaleString()}</TableCell>
                  <TableCell>{formatCategoryLimit(cat.gigLimit, "gig")}</TableCell>
                  <TableCell>{formatCategoryLimit(cat.timeLimit, "time")}</TableCell>
                  <TableCell>
                    <Chip
                      label={cat.availableForSale ? "فعال" : "غیرفعال"}
                      color={cat.availableForSale ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => openEdit(cat)}>
                      <Edit />
                    </IconButton>
                    <IconButton size="small" onClick={() => setDeleteId(cat._id)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="تعداد در صفحه"
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId ? "ویرایش تعرفه VMess" : "افزودن تعرفه VMess"}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="نام (مثال: ۲ گیگ ۳ ماهه)"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="قیمت (تومان)"
              value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
              fullWidth
            />
            <TextField
              label="حجم (گیگابایت)"
              value={form.gigLimit}
              onChange={(e) => setForm({ ...form, gigLimit: e.target.value })}
              helperText="۰ = نامحدود"
              fullWidth
            />
            <TextField
              label="مدت (ماه)"
              value={form.timeLimit}
              onChange={(e) => setForm({ ...form, timeLimit: e.target.value })}
              helperText="۰ = نامحدود"
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.availableForSale}
                  onChange={(e) =>
                    setForm({ ...form, availableForSale: e.target.checked })
                  }
                />
              }
              label="قابل فروش"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>انصراف</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={creating || updating || !form.name || !form.price}
          >
            ذخیره
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialogComponent
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="حذف تعرفه VMess"
        message="آیا از حذف این تعرفه اطمینان دارید؟"
        confirmText="حذف"
        confirmColor="error"
      />
    </Box>
  );
};
